import * as Sentry from "@sentry/nextjs";
import { useReducer } from "react";
import { TypeFormField } from "../app/content/typegen";

export const REGULAR_EXPRESSIONS = {
  date: /^(([1-9]{1}[0-9]{3})-(0[1-9]{1}|10|11|12)-(0[1-9]{1}|1[0-9]{1}|2[0-8]{1}))|(([1-9]{1}[0-9]{3})-(01|03|05|07|08|10|12)-(29|30|31))|(([1-9]{1}[0-9]{3})-(04|06|09|11)-(29|30))|(((12|16|20|24|28|32|36|40|44|48|52|56|60|64|68|72|76|80|84|88|92|96)00|[1-9]{1}[0-9]{1}(04|08|12|16|20|24|28|32|36|40|44|48|52|56|60|64|68|72|76|80|84|88|92|96))-02-29)$/,
  email:
    // eslint-disable-next-line no-control-regex
    /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/i,
  iban: /^((DE[0-9]{20})|(AT[0-9]{18})|(CH[0-9]{19}))$/i,
};

export type InitializedField = TypeFormField & {
  value: string;
  isTouched: boolean;
  isValid: boolean;
};

const isFieldValid = ({
  type,
  isRequired,
  validation,
  value,
}: InitializedField): boolean => {
  if (!isRequired) {
    return true;
  }
  if (type === "checkbox") {
    return value.toString().trim() !== "Nein";
  }
  if (type === "date") {
    return REGULAR_EXPRESSIONS.date.test(
      value.toString().replace(/\s/g, "").trim(),
    );
  }
  if (validation) {
    return REGULAR_EXPRESSIONS[validation].test(
      value.toString().replace(/\s/g, "").trim(),
    );
  }
  return value.toString().trim() !== "";
};

const isFormValid = (formFields: InitializedField[]): boolean =>
  formFields.every((field) => field.isValid);

const initialiseFields = (fields: TypeFormField[]): InitializedField[] =>
  fields.map((field) => {
    const initialisedField: InitializedField = {
      ...field,
      value: field.type === "checkbox" ? "Nein" : "",
      isTouched: false,
      isValid: false,
    };
    return { ...initialisedField, isValid: isFieldValid(initialisedField) };
  });

export const SERVER_STATE = {
  ERROR: "ERROR",
  NOT_SUBMITTED: "NOT_SUBMITTED",
  SUBMITTING: "SUBMITTING",
  SUCCESS: "SUCCESS",
} as const;

type ServerState = (typeof SERVER_STATE)[keyof typeof SERVER_STATE];

type State = {
  isValid: boolean;
  defaultFormFieldsPerPerson: InitializedField[];
  formFieldsPerPerson: InitializedField[][];
  generalFormFields: InitializedField[];
  serverState: ServerState;
  submitCount: number;
};

const getInitialState = (
  formFieldsPerPerson: TypeFormField[],
  generalFormFields: TypeFormField[],
): State => {
  const initialisedFormFieldsPerPerson = initialiseFields(formFieldsPerPerson);
  const initialisedGeneralFormFields = initialiseFields(generalFormFields);
  return {
    isValid: isFormValid([
      ...initialisedFormFieldsPerPerson,
      ...initialisedGeneralFormFields,
    ]),
    defaultFormFieldsPerPerson: initialiseFields(formFieldsPerPerson),
    formFieldsPerPerson: [initialisedFormFieldsPerPerson],
    generalFormFields: initialisedGeneralFormFields,
    serverState: SERVER_STATE.NOT_SUBMITTED,
    submitCount: 0,
  };
};

const FORM_ADD_PERSON = "FORM_ADD_PERSON" as const;
const FORM_REMOVE_PERSON = "FORM_REMOVE_PERSON" as const;
const FORM_SERVER_STATE = "FORM_SERVER_STATE" as const;
const FORM_SUBMIT = "FORM_SUBMIT" as const;
const FORM_UPDATE_FIELD = "FORM_UPDATE_FIELD" as const;

type AddPersonAction = {
  type: typeof FORM_ADD_PERSON;
};

type RemovePersonAction = {
  type: typeof FORM_REMOVE_PERSON;
  payload: number;
};

type ServerStateAction = {
  type: typeof FORM_SERVER_STATE;
  payload: ServerState;
};

type SubmitAction = {
  type: typeof FORM_SUBMIT;
};

type UpdateFieldAction = {
  type: typeof FORM_UPDATE_FIELD;
  payload: {
    label: string;
    value: string;
    personIndex?: number;
  };
};

type Action =
  | AddPersonAction
  | RemovePersonAction
  | ServerStateAction
  | SubmitAction
  | UpdateFieldAction;

const reducer = (state: State, action: Action) => {
  switch (action.type) {
    case FORM_ADD_PERSON:
      state.formFieldsPerPerson.push(
        state.defaultFormFieldsPerPerson.map((formField) => ({ ...formField })),
      );
      return {
        ...state,
        isValid: isFormValid([
          ...state.formFieldsPerPerson.flat(),
          ...state.generalFormFields,
        ]),
      };

    case FORM_REMOVE_PERSON:
      state.formFieldsPerPerson.splice(action.payload, 1);
      return {
        ...state,
        isValid: isFormValid([
          ...state.formFieldsPerPerson.flat(),
          ...state.generalFormFields,
        ]),
      };

    case FORM_SERVER_STATE:
      return { ...state, serverState: action.payload };

    case FORM_SUBMIT:
      return {
        ...state,
        formFieldsPerPerson: state.formFieldsPerPerson.map((formFields) =>
          formFields.map((formField) => ({ ...formField, isTouched: true })),
        ),
        generalFormFields: state.generalFormFields.map((formField) => ({
          ...formField,
          isTouched: true,
        })),
        submitCount: state.submitCount + 1,
      };

    case FORM_UPDATE_FIELD:
      const currentField =
        typeof action.payload.personIndex === "number"
          ? (state.formFieldsPerPerson[action.payload.personIndex] || []).find(
              (formField) => formField.label === action.payload.label,
            )
          : state.generalFormFields.find(
              (formField) => formField.label === action.payload.label,
            );

      if (!currentField) {
        return state;
      }
      currentField.value = action.payload.value;
      currentField.isTouched = true;
      currentField.isValid = isFieldValid(currentField);

      return {
        ...state,
        isValid: isFormValid([
          ...state.formFieldsPerPerson.flat(),
          ...state.generalFormFields,
        ]),
      };

    default:
      return state;
  }
};

type UseFormArgs<SubmitArgs extends any[] = any[]> = {
  formFieldsPerPerson: TypeFormField[];
  generalFormFields: TypeFormField[];
  onSubmit: (state: State, ...args: SubmitArgs) => Promise<void>;
};

export const useForm = <SubmitArgs extends any[] = any[]>({
  formFieldsPerPerson,
  generalFormFields,
  onSubmit,
}: UseFormArgs<SubmitArgs>) => {
  const [state, dispatch] = useReducer(
    reducer,
    getInitialState(formFieldsPerPerson, generalFormFields),
  );

  const submitForm = async (...args: SubmitArgs) => {
    dispatch({ type: FORM_SUBMIT });
    if (state.isValid) {
      dispatch({ type: FORM_SERVER_STATE, payload: SERVER_STATE.SUBMITTING });
      try {
        await onSubmit(state, ...args);
        dispatch({ type: FORM_SERVER_STATE, payload: SERVER_STATE.SUCCESS });
      } catch (error) {
        Sentry.captureException(error);
        dispatch({ type: FORM_SERVER_STATE, payload: SERVER_STATE.ERROR });
      }
    }
  };

  const addPerson = () => dispatch({ type: FORM_ADD_PERSON });

  const removePerson = (personIndex: number) =>
    dispatch({
      type: FORM_REMOVE_PERSON,
      payload: personIndex,
    });

  const updateValue = (payload: UpdateFieldAction["payload"]) =>
    dispatch({ type: FORM_UPDATE_FIELD, payload });

  return {
    state,
    submitForm,
    addPerson,
    removePerson,
    updateValue,
  };
};
